import { useId, useState } from 'react';
import classNames from 'classnames';
import type { HeadingProps } from '@components/text';
import { AccordionItem, AccordionItemProps } from '../AccordionItem/AccordionItem';

import './Accordion.scss';

/** Accordion item data - excludes controlled props that are managed by Accordion */
export type AccordionItemData = Omit<
  AccordionItemProps,
  'isActive' | 'size' | 'headingLevel' | 'ariaControls' | 'ariaLabelledBy'
>;

export const ACCORDION_SIZES = ['large', 'medium', 'small'] as const;

export type AccordionSize = (typeof ACCORDION_SIZES)[number];

export interface AccordionProps {
  /** Array of accordion items to render */
  items: Array<AccordionItemData>;
  /** optional class */
  className?: string;
  /** Change size of layout for desktop and mobile. Default large */
  size?: AccordionSize;
  /** Semantic heading level for each question label. Default 3 (e.g. FAQ under a page h1). */
  headingLevel?: HeadingProps['ariaLevel'];
}

/**
 * Accordion component
 * @param items - Array of accordion items to render (size, isActive, headingLevel, ariaControls, and ariaLabelledBy are controlled by Accordion)
 * @param className - optional class
 * @param size - change size of layout for desktop and mobile
 * @param headingLevel - semantic heading level for each question label
 */
export const Accordion: React.FC<AccordionProps> = ({
  items,
  className,
  size = 'large',
  headingLevel = 3,
}) => {
  const id = useId();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleItemClick = (index: number, onClick?: () => void) => {
    setActiveIndex(prevIndex => (prevIndex === index ? null : index));
    onClick?.();
  };

  return (
    <div className={classNames('vds-accordion', `vds-accordion--${size}`, className)}>
      {items.map((item, index) => {
        const { onClick, ...itemProps } = item;
        const panelId = `${id}-accordion-panel-${index}`;
        const labelId = `${id}-accordion-label-${index}`;
        return (
          <AccordionItem
            key={`accordion-item-${index}`}
            {...itemProps}
            size={size}
            headingLevel={headingLevel}
            isActive={activeIndex === index}
            onClick={() => handleItemClick(index, onClick)}
            ariaControls={panelId}
            ariaLabelledBy={labelId}
          />
        );
      })}
    </div>
  );
};
